import { FC, MutableRefObject, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// material-ui
import {
    Box,
    Chip,
    ClickAwayListener,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    PaletteMode,
    Paper,
    Popper,
    Radio,
    RadioGroup,
    Stack,
    Typography,
    useTheme
} from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import useConfig from 'hooks/useConfig';
import useAuth from 'hooks/useAuth';
import { getGreetingByTime } from 'utils/date';
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';

// assets
import { IconLogout, IconSettings, IconUser } from '@tabler/icons';

type Props = {
    anchorRef: MutableRefObject<any>;
    open: boolean;
    onClose: (event: React.MouseEvent<HTMLDivElement> | MouseEvent | TouchEvent) => void;
};
const ProfilePopper: FC<Props> = ({ anchorRef, open, onClose }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { navType, onChangeMenuType } = useConfig();
    const { user, logout } = useAuth();

    const [selectedIndex] = useState(-1);

    const handleLogout = async () => {
        try {
            await logout();
        } catch (err) {
            console.error(err);
        }
    };

    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }

        prevOpen.current = open;
    }, [open]);

    return (
        <Popper
            placement="bottom"
            open={open}
            anchorEl={anchorRef.current}
            role={undefined}
            transition
            disablePortal
            sx={{ pr: 2.5 }}
            popperOptions={{
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 14]
                        }
                    }
                ]
            }}
        >
            {({ TransitionProps }) => (
                <ClickAwayListener onClickAway={onClose}>
                    <Transitions in={open} {...TransitionProps}>
                        <Paper>
                            {open && (
                                <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                                    <Box sx={{ p: 2, pb: 0 }}>
                                        <Stack sx={{ pb: 1 }}>
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <Typography variant="h4">{t(getGreetingByTime())},</Typography>
                                                <Typography component="span" variant="h4" sx={{ fontWeight: 400 }}>
                                                    {user?.fullName}
                                                </Typography>
                                            </Stack>
                                            <Typography variant="subtitle2">Amministratore di sistema</Typography>
                                        </Stack>
                                        <Divider />
                                    </Box>
                                    <PerfectScrollbar style={{ height: '100%', maxHeight: 'calc(100vh - 250px)', overflowX: 'hidden' }}>
                                        <Box sx={{ p: 2, pt: 0 }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Typography variant="subtitle2">Tema</Typography>
                                                <FormControl component="fieldset">
                                                    <RadioGroup
                                                        row
                                                        aria-label="layout"
                                                        value={navType}
                                                        onChange={(e) => onChangeMenuType(e.target.value as PaletteMode)}
                                                        name="row-radio-buttons-group"
                                                    >
                                                        <FormControlLabel
                                                            value="light"
                                                            control={<Radio />}
                                                            label="Chiaro"
                                                            sx={{
                                                                '& .MuiSvgIcon-root': { fontSize: 28 },
                                                                '& .MuiFormControlLabel-label': { color: theme.palette.grey[900] }
                                                            }}
                                                        />
                                                        <FormControlLabel
                                                            value="dark"
                                                            control={<Radio />}
                                                            label="Scuro"
                                                            sx={{
                                                                '& .MuiSvgIcon-root': { fontSize: 28 },
                                                                '& .MuiFormControlLabel-label': { color: theme.palette.grey[900] }
                                                            }}
                                                        />
                                                    </RadioGroup>
                                                </FormControl>
                                            </Stack>
                                            <Divider />
                                            <List
                                                component="nav"
                                                sx={{
                                                    width: '100%',
                                                    maxWidth: 350,
                                                    minWidth: 300,
                                                    backgroundColor: theme.palette.background.paper,
                                                    borderRadius: '10px',
                                                    [theme.breakpoints.down('md')]: {
                                                        minWidth: '100%'
                                                    },
                                                    '& .MuiListItemButton-root': {
                                                        mt: 0.5
                                                    }
                                                }}
                                            >
                                                {/* <ListItemButton
                                                    sx={{ borderRadius: '8px' }}
                                                    selected={selectedIndex === 0}
                                                    // onClick={(event: React.MouseEvent<HTMLDivElement>) =>
                                                    //     handleListItemClick(event, 0, '/user/account-profile/profile1')
                                                    // }
                                                >
                                                    <ListItemIcon>
                                                        <IconSettings stroke={1.5} size="1.3rem" />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={<Typography variant="body2">{t('ACCOUNT_SETTINGS')}</Typography>}
                                                    />
                                                </ListItemButton>
                                                <ListItemButton
                                                    sx={{ borderRadius: '8px' }}
                                                    selected={selectedIndex === 1}
                                                    // onClick={(event: React.MouseEvent<HTMLDivElement>) =>
                                                    //     handleListItemClick(event, 1, '/user/social-profile/posts')
                                                    // }
                                                >
                                                    <ListItemIcon>
                                                        <IconUser stroke={1.5} size="1.3rem" />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={
                                                            <Grid container spacing={1} justifyContent="space-between">
                                                                <Grid item>
                                                                    <Typography variant="body2">{t('PROFILE')}</Typography>
                                                                </Grid>
                                                                <Grid item>
                                                                    <Chip
                                                                        label="02"
                                                                        size="small"
                                                                        sx={{
                                                                            bgcolor:
                                                                                theme.palette.mode === 'dark'
                                                                                    ? theme.palette.dark.dark
                                                                                    : theme.palette.warning.dark,
                                                                            color: theme.palette.background.default
                                                                        }}
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        }
                                                    />
                                                </ListItemButton> */}
                                                <ListItemButton
                                                    sx={{ borderRadius: '8px' }}
                                                    selected={selectedIndex === 4}
                                                    onClick={handleLogout}
                                                >
                                                    <ListItemIcon>
                                                        <IconLogout stroke={1.5} size="1.3rem" />
                                                    </ListItemIcon>
                                                    <ListItemText primary={<Typography variant="body2">{t('LOGOUT')}</Typography>} />
                                                </ListItemButton>
                                            </List>
                                        </Box>
                                    </PerfectScrollbar>
                                </MainCard>
                            )}
                        </Paper>
                    </Transitions>
                </ClickAwayListener>
            )}
        </Popper>
    );
};

export default ProfilePopper;
